import React from 'react';
import {
  FormControlLabel as MuiFormControlLabel,
  FormControlLabelProps as MuiFormControlLabelProps,
  styled,
  formControlLabelClasses,
  Box,
} from '@mui/material';
import { Typography } from '../Typography';

const StyledFormControlLabel = styled(MuiFormControlLabel)(() => ({
  gap: '8px',
  [`& .${formControlLabelClasses.label}`]: {
    color: '#414651',
    fontSize: '14px',
    fontWeight: 500,
  },
}));

type FormControlLabelProps = Pick<
  MuiFormControlLabelProps,
  | 'control'
  | 'labelPlacement'
  | 'disabled'
  | 'value'
  | 'onChange'
  | 'checked'
  | 'inputRef'
  | 'slotProps'
  | 'disableTypography'
  | 'sx'
> & {
  /**
   * The main label text
   */
  label: React.ReactNode;
  /**
   * Optional description text displayed below the label
   */
  description?: string;
  /**
   * Optional margin-top for the label when description is present.
   * Allows customization for different control sizes (Checkbox, Radio, Switch).
   * Defaults to '5px' when description is provided.
   */
  marginTop?: string | number;
  /**
   * Optional id for the component
   */
  id?: string;
};

/**
 * A styled FormControlLabel component that wraps form controls (Checkbox, Radio, Switch) with labels.
 * This component uses MUI's out-of-the-box FormControlLabel functionality styled with our design system.
 *
 * Supports an optional description text (supporting text) for additional context.
 * The label text is styled with our design system colors and typography.
 *
 * Props:
 * - label: The main label text (required)
 * - description: Optional supporting text displayed below the label
 * - marginTop: Optional margin-top for label alignment when description is present (defaults to '5px')
 * - control: The form control element (Checkbox, Radio, or Switch)
 * - All other MUI FormControlLabel props are supported
 *
 * Can be used within FormGroup with FormLabel (group label) and FormHelperText (help text) for complete form sections.
 */
export const FormControlLabel: React.FC<FormControlLabelProps> = ({
  label,
  description,
  marginTop = '5px',
  sx,
  ...props
}) => {
  const labelContent = description ? (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography
        variant="body1"
        sx={{ marginTop: marginTop, color: '#414651', fontSize: '14px', fontWeight: 500, lineHeight: '20px' }}
      >
        {label}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          component="p"
          sx={{ fontSize: '14px', fontWeight: 400, color: '#535862', lineHeight: '20px' }}
        >
          {description}
        </Typography>
      )}
    </Box>
  ) : (
    label
  );

  const mergedSx = [
    {
      alignItems: description ? 'flex-start' : undefined,
    },
    sx,
  ] as unknown as React.ComponentProps<typeof StyledFormControlLabel>['sx'];

  return <StyledFormControlLabel {...props} label={labelContent} disableTypography={!!description} sx={mergedSx} />;
};

export { FormSection } from './FormSection';
export type { FormSectionProps } from './FormSection';
